import SEO from '../components/SEO';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, MessageCircle, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { trackFormSubmission } from '../utils/analytics';
import axios from 'axios';

const ContactPage = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', company: '', role: '', topic: '', message: '', consent: false });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const showSupportBanner = formData.topic === 'Support';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name required';
    if (!formData.email.trim()) newErrors.email = 'Email required';
    if (!formData.company.trim()) newErrors.company = 'Company required';
    if (!formData.topic) newErrors.topic = 'Please select a topic';
    if (!formData.message.trim()) newErrors.message = 'Message required';
    if (!formData.consent) newErrors.consent = 'You must agree';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/forms/contact`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: `Role: ${formData.role || 'Not specified'}, Topic: ${formData.topic}, Message: ${formData.message}`,
        source: `Contact Form - ${document.referrer ? new URL(document.referrer).pathname : window.location.pathname}`
      });
      
      trackFormSubmission('Contact Page Form');
      setSubmitted(true);
    } catch (error) {
      setErrors({ submit: 'Error. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Contact Ascarii | SAP Business One Enquiries & Consultations"
        description="Get in touch with Ascarii SAP experts. Book a consultation, request a demo, or speak to our team. UK-based support, fast response within 1 business day."
        keywords="contact Ascarii, SAP B1 enquiry, ERP consultation, book SAP demo, get in touch"
        canonical="https://www.ascarii.com/contact"
      />

      <section className="relative py-20 bg-gradient-to-br from-blue-600 via-sky-700 to-cyan-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Let's talk about modernising your ERP Solution</h1>
              <p className="text-xl text-blue-100 mb-8">Tell us about your goals - our team will get back to you quickly</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="#contact-form" className="inline-flex items-center rounded-xl bg-white text-blue-600 px-8 py-4 text-lg font-semibold hover:bg-blue-50 shadow-lg">Send a message</a>
                <a href="tel:+441789777466" className="inline-flex items-center rounded-xl border-2 border-white text-white px-8 py-4 text-lg font-semibold hover:bg-white/10 shadow-lg">
                  <Phone className="w-5 h-5 mr-2" />Call: +44 (0)1789 777 466
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {showSupportBanner && (
        <div className="bg-orange-50 border-l-4 border-orange-500 p-6">
          <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-orange-600" />
                <div>
                  <p className="font-semibold text-gray-900">Need help right now?</p>
                  <p className="text-sm text-gray-700">Visit our Service Desk for ticketed support</p>
                </div>
              </div>
              <a href="https://support.ascarii.com/portal/en/home" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700">
                Visit Service Desk <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
        </div>
      )}

      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" id="contact-form">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3">
              {submitted ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl p-12 border-2 border-green-200 shadow-xl text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Thanks - We'll Be In Touch Soon</h2>
                  <p className="text-gray-600 mb-8">One of our specialists will reply within one business day.</p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <a href="/sap-business-one" className="text-blue-600 hover:text-blue-700 font-semibold">Explore SAP Business One</a>
                    <a href="/blog" className="text-blue-600 hover:text-blue-700 font-semibold">Read Our Blog</a>
                  </div>
                </motion.div>
              ) : (
                <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name Fields */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First name <span className="text-red-500">*</span></Label>
                        <input 
                          id="firstName"
                          type="text" 
                          value={formData.firstName} 
                          onChange={(e) => setFormData({...formData, firstName: e.target.value})} 
                          className={`w-full h-10 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb] ${errors.firstName ? 'border-red-300' : 'border-gray-300'}`} 
                          placeholder="John" 
                        />
                        {errors.firstName && <p className="text-xs text-red-600">{errors.firstName}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last name</Label>
                        <input 
                          id="lastName"
                          type="text" 
                          value={formData.lastName} 
                          onChange={(e) => setFormData({...formData, lastName: e.target.value})} 
                          className={`w-full h-10 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb] ${errors.lastName ? 'border-red-300' : 'border-gray-300'}`} 
                          placeholder="Smith" 
                        />
                      </div>
                    </div>

                    {/* Contact Fields */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="email">Work email <span className="text-red-500">*</span></Label>
                        <input 
                          id="email"
                          type="email" 
                          value={formData.email} 
                          onChange={(e) => setFormData({...formData, email: e.target.value})} 
                          className={`w-full h-10 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb] ${errors.email ? 'border-red-300' : 'border-gray-300'}`} 
                          placeholder="john@company.com" 
                        />
                        {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <input 
                          id="phone"
                          type="tel" 
                          value={formData.phone} 
                          onChange={(e) => setFormData({...formData, phone: e.target.value})} 
                          className="w-full h-10 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb]" 
                          placeholder="+44 7700 123456" 
                        />
                      </div>
                    </div>

                    {/* Company and Role */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="company">Company <span className="text-red-500">*</span></Label>
                        <input 
                          id="company"
                          type="text" 
                          value={formData.company} 
                          onChange={(e) => setFormData({...formData, company: e.target.value})} 
                          className={`w-full h-10 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb] ${errors.company ? 'border-red-300' : 'border-gray-300'}`} 
                          placeholder="Your Company" 
                        />
                        {errors.company && <p className="text-xs text-red-600">{errors.company}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="role">Your Role</Label>
                        <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select your role..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Owner/Founder">Owner/Founder</SelectItem>
                            <SelectItem value="CEO/MD">CEO/MD</SelectItem>
                            <SelectItem value="Operations">Operations Director</SelectItem>
                            <SelectItem value="Finance">Finance Director</SelectItem>
                            <SelectItem value="IT/Systems">IT/Systems Director</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">Helps us direct your enquiry to the right specialist</p>
                      </div>
                    </div>

                    {/* Topic */}
                    <div className="space-y-2">
                      <Label htmlFor="topic">What do you need help with? <span className="text-red-500">*</span></Label>
                      <Select value={formData.topic} onValueChange={(value) => setFormData({...formData, topic: value})}>
                        <SelectTrigger className={`h-12 ${errors.topic ? 'border-red-500' : ''}`}>
                          <SelectValue placeholder="Select topic..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SAP Business One">SAP Business One</SelectItem>
                          <SelectItem value="Implementation">Implementation</SelectItem>
                          <SelectItem value="Support">Support</SelectItem>
                          <SelectItem value="Partner Transfer">Partner Transfer</SelectItem>
                          <SelectItem value="Integration/Development">Integration/Development</SelectItem>
                          <SelectItem value="Cloud hosting">Cloud hosting</SelectItem>
                          <SelectItem value="Something else">Something else</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.topic && <p className="text-xs text-red-600">{errors.topic}</p>}
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message">Message <span className="text-red-500">*</span></Label>
                      <textarea 
                        id="message"
                        value={formData.message} 
                        onChange={(e) => setFormData({...formData, message: e.target.value})} 
                        rows="6" 
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb] ${errors.message ? 'border-red-300' : 'border-gray-300'}`} 
                        placeholder="Tell us about your requirements..." 
                      />
                      {errors.message && <p className="text-xs text-red-600">{errors.message}</p>}
                    </div>

                    {/* Consent */}
                    <div>
                      <label className="flex items-start gap-3">
                        <input 
                          type="checkbox" 
                          checked={formData.consent} 
                          onChange={(e) => setFormData({...formData, consent: e.target.checked})} 
                          className="mt-1 w-5 h-5 text-blue-600 rounded" 
                        />
                        <span className="text-sm text-gray-700">
                          I agree to Ascarii contacting me about this enquiry and I've read the{' '}
                          <a href="https://www.ascarii.com/privacy-policy/" target="_blank" rel="noopener" className="text-blue-600 hover:underline">Privacy Policy</a>.
                        </span>
                      </label>
                      {errors.consent && <p className="mt-1 text-xs text-red-600">{errors.consent}</p>}
                    </div>

                    {errors.submit && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">{errors.submit}</p>
                      </div>
                    )}

                    <Button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 hover:bg-blue-700 py-4 text-lg font-semibold">
                      {isSubmitting ? 'Sending...' : 'Send message'}
                    </Button>
                    
                    <p className="text-xs text-gray-500 text-center">
                      By submitting, you agree to our <a href="/terms" className="text-blue-600 hover:underline">Terms</a>.
                    </p>
                  </form>
                </div>
              )}
            </div>

            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Head Office</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Address</p>
                      <p className="text-sm text-gray-600">
                        NETPark, Thomas Wright Way<br />
                        Sedgefield, County Durham<br />
                        TS21 3FD, United Kingdom
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Call us</p>
                      <a href="tel:+441789777466" className="text-sm text-blue-600 hover:underline">+44 (0)1789 777 466</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Email us</p>
                      <a href="mailto:info@ascarii.com" className="text-sm text-blue-600 hover:underline">info@ascarii.com</a>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Office hours</p>
                  <p className="text-sm text-gray-600">Mon-Fri, 9:00-17:30 (UK time)</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Our Partners</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-900">SAP Premier Partner</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-sky-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-sky-600" />
                    <span className="text-sm font-semibold text-gray-900">AWS Advanced Partner</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-semibold text-gray-900">Boyum Gold Partner</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Quick Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'How quickly will I get a response?', a: 'Within one business day (Mon-Fri, 9:00-17:30 UK time).' },
              { q: 'What happens on a discovery call?', a: 'We discuss your challenges, goals, and how SAP Business One can help. Typically 30-45 minutes.' },
              { q: 'Is my data protected?', a: 'Yes - GDPR compliant. Read our Privacy Policy for details.' }
            ].map((faq, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-700">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to start?</h2>
          <p className="text-gray-600 mb-6">Book a discovery call with our team</p>
          <a href="#contact-form" className="inline-flex items-center rounded-xl bg-blue-600 text-white px-8 py-3 font-semibold hover:bg-blue-700 shadow-lg">
            Book a discovery call
          </a>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
